<script setup>
  import {computed, inject, onBeforeMount, onBeforeUnmount, onMounted, provide, reactive, ref, watch} from "vue";

  import ComponentAlert from "@components/Alerts/Alert.vue";
  import ComponentBreadcrumb from "@components/Breadcrumb/Breadcrumb.vue";
  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import ComponentDialog from "@components/Dialogs/Dialog.vue";
  import ComponentRecordInvalidatedDialog from "@components/Dialogs/RecordInvalidatedDialog.vue";
  import ComponentRecordShareStatusDialog from "@components/Dialogs/RecordShareStatusDialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentPatientSidebar from "@components/Sidebars/PatientSidebar.vue";
  import SkeletonLoader from "@components/SkeletonLoader.vue";
  import ComponentStepper from "@components/Stepper.vue";
  import {Deferred as InertiaDeferred, router, useForm, usePage} from "@inertiajs/vue3";
  import RecordComponentBeforeAnalyseDialog from "@pages/Records/Components/AnalyseDialog.vue";
  import RecordComponentEditAgainDialog from "@pages/Records/Components/EditAgainDialog.vue";
  import PagesRecordGeneralShareStatus from "@pages/Records/Components/General/ShareStatus.vue";
  import RecordComponentJsonExporter from "@pages/Records/Components/JsonExporter.vue";
  import ComponentRecordProgress from "@pages/Records/Components/RecordProgress.vue";
  import RecordComponentSectionDiseaseBar from "@pages/Records/Components/Sections/DiseaseBar/DiseaseBar.vue";
  import {addDisease} from "@pages/Records/Components/Sections/DiseaseBar/Utils/diseaseHelper.js";
  import {
    bloodpressureKey,
    bmiKey,
    bodySizeKey,
    bodyWeightKey,
    creatinineKey,
    gfrKey,
    mainLaborvalueList,
    pulseKey,
  } from "@pages/Records/Components/Sections/LaborBar/enums.js";
  import RecordComponentSectionLaborBar from "@pages/Records/Components/Sections/LaborBar/LaborBar.vue";
  import {circumstance} from "@pages/Records/Components/Sections/MedicationBar/Components/Indications/enum.js";
  import RecordComponentSectionMedicationBar from "@pages/Records/Components/Sections/MedicationBar/MedicationBar.vue";
  import RecordComponentSectionSymptomBar from "@pages/Records/Components/Sections/SymptomBar/SymptomBar.vue";
  import RecordComponentSectionUserBar from "@pages/Records/Components/Sections/UserBar/UserBar.vue";
  import {recordOriginKeys, recordOriginTexts} from "@pages/Records/Enums/Enums.js";
  import {MedButton, MedLink} from "@pharma4u/medicheck-components";
  import {coreRouter} from "@utils/coreRouter/coreRouter.js";
  import {getAge} from "@utils/Helpers/AgeCalulator.js";
  import Window from "@utils/Helpers/Window.js";
  import {each} from "es-toolkit/compat";

  defineOptions({
    name: "PagesRecordEdit",
  });

  const props = defineProps({
    patient: {
      type: Object,
      default: () => ({}),
    },
    record: {
      type: Object,
      default: () => ({}),
    },
    medications: {
      type: Object,
      default: null,
    },
    symptoms: {
      type: Object,
      default: null,
    },
    diseases: {
      type: Object,
      default: null,
    },
    laborvalues: {
      type: Object,
      default: null,
    },
    transfer: {
      type: Object,
      default: null,
    },
    sharedata: {
      type: Object,
      default: () => ({}),
    },
    notifications: {
      type: Object,
      default: () => ({}),
    },
    progressStates: {
      type: Object,
      default: () => ({}),
    },
    via: {
      type: String,
      default: null,
    },
    failed: {
      type: Array,
      default: () => [],
    },
    isEditable: {
      type: Boolean,
      default: false,
    },
    shareStatus: {
      type: String,
      default: null,
    },
  });

  const page = usePage();
  const privacy = inject("$privacy");
  const broadcast = inject("$broadcast");

  const refConfirmMergeRecord = ref(null);
  const refEditAgainDialog = ref(null);
  const refInvalidDosageConfirmationDialog = ref(null);
  const refMedicationDeleteConfirmationDialog = ref(null);
  const refRecordEditFinishedDialog = ref(null);
  const refShareStatusDialog = ref(null);

  const patientAge = ref(null);
  const currentUser = ref(null);
  const recordData = reactive({
    // initialize deferred props as empty,
    // to avoid undefined issues when accessing
    medications: [],
    laborvalues: [],
    diseases: [],
    symptoms: [],
    ...props.record,
  });
  const currentIsEditable = ref(props.isEditable);

  const medicationDeleteConfirmationDialogText = ref("");

  const invalidDosageFields = ref([]);

  const isMassUpdateProcessing = ref({
    medication: false,
    symptom: false,
    laborValue: false,
    disease: false,
  });

  // page reload persistent implementation of useRemember()
  const STORAGE_KEY = `Record/Edit:${props.record.id}`;
  const EXPIRATION_MS = 5 * 60 * 1000; // 5 minutes

  const getInitialState = () => {
    const savedState = localStorage.getItem(STORAGE_KEY);

    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        const now = Date.now();

        if (parsed.expiresAt && parsed.expiresAt > now) {
          return parsed.state;
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      } catch (error) {
        console.warn("Fehler beim Parsen des gespeicherten Zustands:", error);
      }
    }
    return {
      active: "medicationbar",
      showDiseaseDialog: true,
    };
  };

  const rememberForm = ref(getInitialState());

  watch(
    rememberForm,
    (newState) => {
      const payload = {
        state: newState,
        expiresAt: Date.now() + EXPIRATION_MS,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    },
    {deep: true},
  );

  const recordForm = useForm({
    username: null,
    userstatus: null,
  });

  const hasMissingConfirmation = computed(() => {
    // easy: fake patients are never missing any confirmation
    if (props.patient.is_fake) {
      return false;
    }

    // easy²: documents are not available on core license
    if (page.props.client.license.is_core) {
      return false;
    }

    // easy: agreementpdfconfirmed and datasecurityconfirmed are always required
    if (!props.patient.agreementpdlconfirmed || !props.patient.datasecurityconfirmed) {
      // at least one required document is missing
      return true;
    }

    // easy: ALL remaining documents are already accepted
    if (
      props.patient.releasefromconfidentialityconfirmed &&
      props.patient.datasecurityresearchconfirmed &&
      props.patient.datasecurityaiconfirmed
    ) {
      return false;
    }

    // releasefromconfidentialityconfirmed, datasecurityresearchconfirmed and datasecurityaiconfirmed
    // should only trigger "hasMissingConfirmation" when they are neither accepted nor rejected.
    // now we have to look into the patients.confirmations
    if (
      props.patient.confirmations.some((c) => c.subject === "releasefromconfidentialityconfirmed") &&
      props.patient.confirmations.some((c) => c.subject === "datasecurityresearchconfirmed") &&
      props.patient.confirmations.some((c) => c.subject === "datasecurityaiconfirmed")
    ) {
      // they all three exist in the confirmations (it does not matter if accepted or rejected)
      // => no document is without any decision of the patient / no document is missing
      return false;
    }

    return true;
  });

  const medicationProblems = computed(() => {
    let problems = {
      unloadableMedications: [],
      unloadableMedicationsSolved: [],
      invalidDosageFields: invalidDosageFields.value,
      level: null, // "warning" (show ⚠️ but allow to analyze), "error" (show ⚠️ and disable analyze button)
    };

    if (props.medications?.data) {
      problems.unloadableMedications = props.medications.data.filter((medication) => medication.core.unloadable);

      problems.unloadableMedicationsSolved = props.medications.data
        .filter((medication) => medication.core.unloadableSolved)
        .map((medication) => ({
          old: medication,
          new: medication.core.unloadableSolved,
        }));
    }

    if (
      problems.unloadableMedications?.length ||
      problems.unloadableMedicationsSolved?.length ||
      problems.invalidDosageFields?.length
    ) {
      problems.level = "error";
    }
    // else if (false) {
    //   problems.level = "warning";
    // }

    return problems;
  });

  const symptomProblems = computed(() => {
    let problems = {
      unloadableSymptoms: [],
      unloadableSymptomsSolved: [],
      contradictions: [],
      level: null, // "warning" (show ⚠️ but allow to analyze), "error" (show ⚠️ and disable analyze button)
    };

    if (props.symptoms?.data) {
      problems.unloadableSymptoms = props.symptoms.data.filter((symptom) => symptom.core.unloadable);

      problems.unloadableSymptomsSolved = props.symptoms.data
        .filter((symptom) => symptom.core.unloadableSolved)
        .map((symptom) => ({
          old: symptom,
          new: symptom.core.unloadableSolved,
        }));

      // gender mismatch
      for (const symptom of props.symptoms.data) {
        if (
          (symptom?.core?.gender === 1 && page.props.patient.gender === "female") ||
          (symptom?.core?.gender === 2 && page.props.patient.gender === "male")
        ) {
          problems.contradictions.push(
            `${symptom.symptom} bei ${page.props.patient.gender === "female" ? "weiblicher Patientin" : "männlichem Patienten"}.`,
          );
        }
      }
    }

    if (problems.unloadableSymptoms?.length || problems.unloadableSymptomsSolved?.length) {
      problems.level = "error";
    } else if (problems.contradictions?.length) {
      problems.level = "warning";
    }

    return problems;
  });

  const laborvalueProblems = computed(() => {
    let problems = {
      unloadableLaborvalues: [],
      level: null, // "warning" (show ⚠️ but allow to analyze), "error" (show ⚠️ and disable analyze button)
    };

    if (props.laborvalues?.data) {
      problems.unloadableLaborvalues = props.laborvalues.data.filter((laborvalue) => laborvalue.core.unloadable);
    }

    if (problems.unloadableLaborvalues?.length) {
      problems.level = "error";
    }
    // else if (false) {
    //   problems.level = "warning";
    // }

    // check if main laborvalues contain duplicates
    // (only when merging records)
    if (isMergeRecordTransfer.value) {
      const mainLaborValues = (props.laborvalues?.data ?? [])
        .filter((laborValue) => {
          return mainLaborvalueList.includes(laborValue.mc_laborvalue_key);
        })
        .map((laborValue) => laborValue.mc_laborvalue_key);
      if (mainLaborValues.length !== new Set(mainLaborValues).size) {
        problems.level = "error";
      }
    }

    return problems;
  });

  const diseaseProblems = computed(() => {
    let problems = {
      unloadableDiseases: [],
      unloadableDiseasesSolved: [],
      contradictions: [],
      level: null, // "warning" (show ⚠️ but allow to analyze), "error" (show ⚠️ and disable analyze button)
    };

    if (props.diseases?.data) {
      // diseases are only "unloadable", when they contain an abdata_key_miv
      // (otherwise it might be a stored icd code)
      problems.unloadableDiseases = props.diseases.data.filter(
        (disease) => disease.core.unloadable && disease.abdata_key_miv,
      );

      problems.unloadableDiseasesSolved = props.diseases.data
        .filter((disease) => disease.core.unloadableSolved)
        .map((disease) => ({
          old: disease,
          new: disease.core.unloadableSolved,
        }));

      // gender mismatch
      for (const disease of props.diseases.data) {
        if (
          (disease?.core?.gender === 1 && page.props.patient.gender === "female") ||
          (disease?.core?.gender === 2 && page.props.patient.gender === "male")
        ) {
          problems.contradictions.push(
            `${disease.disease} bei ${page.props.patient.gender === "female" ? "weiblicher Patientin" : "männlichem Patienten"}.`,
          );
        }
      }

      // disease to disease mismatch
      const hasOlderPatient = props.diseases.data.some(
        (disease) => disease.abdata_key_miv === circumstance.OLDER_PATIENT,
      );

      const hasGeriatricPatient = props.diseases.data.some(
        (disease) => disease.abdata_key_miv === circumstance.GERIATRIC_PATIENT,
      );

      if (hasOlderPatient && (patientAge.value < 65 || patientAge.value > 75)) {
        problems.contradictions.push(
          `Lebensumstand „Patient (älter, ab 65 Jahre)“ ggf. unpassend, Patient ist ${patientAge.value} Jahre alt`,
        );
      }

      if (hasGeriatricPatient && patientAge.value < 75) {
        problems.contradictions.push(
          `Lebensumstand „Patient (geriatrisch, ab 75 Jahre)“ ggf. unpassend, Patient ist ${patientAge.value} Jahre alt`,
        );
      }
    }

    // circumstances mismatch
    if (page.props.circumstances !== undefined) {
      if (
        Object.values(page.props.circumstances).includes(circumstance.OLDER_PATIENT) &&
        Object.values(page.props.circumstances).includes(circumstance.GERIATRIC_PATIENT)
      ) {
        problems.contradictions.push("Älterer Patient und gleichzeitig geriatrischer Patient");
      }

      if (
        Object.values(page.props.circumstances).includes(circumstance.CLIMACTERIC) &&
        Object.values(page.props.circumstances).includes(circumstance.POST_CLIMACTERIC)
      ) {
        problems.contradictions.push("Klimakterium und gleichzeitig Postklimakterium");
      }

      if (
        Object.values(page.props.circumstances).includes(circumstance.CLIMACTERIC) &&
        Object.values(page.props.circumstances).includes(circumstance.CHILDBEARING_AGED_WOMAN)
      ) {
        problems.contradictions.push("Klimakterium und gleichzeitig im gebährfähigen Alter");
      }

      if (
        Object.values(page.props.circumstances).includes(circumstance.POST_CLIMACTERIC) &&
        Object.values(page.props.circumstances).includes(circumstance.CHILDBEARING_AGED_WOMAN)
      ) {
        problems.contradictions.push("Postklimakterium und gleichzeitig im gebährfähigen Alter");
      }

      if (
        Object.values(page.props.circumstances).includes(circumstance.CLIMACTERIC) &&
        Object.values(page.props.circumstances).includes(circumstance.PREGNANT_WOMAN)
      ) {
        problems.contradictions.push("Klimakterium und gleichzeitig Schwangerschaft");
      }

      if (
        Object.values(page.props.circumstances).includes(circumstance.POST_CLIMACTERIC) &&
        Object.values(page.props.circumstances).includes(circumstance.PREGNANT_WOMAN)
      ) {
        problems.contradictions.push("Postklimakterium und gleichzeitig Schwangerschaft");
      }

      if (
        Object.values(page.props.circumstances).includes(circumstance.CLIMACTERIC) &&
        Object.values(page.props.circumstances).includes(circumstance.LACTATING_WOMAN)
      ) {
        problems.contradictions.push("Klimakterium und gleichzeitig stillend");
      }

      if (
        Object.values(page.props.circumstances).includes(circumstance.POST_CLIMACTERIC) &&
        Object.values(page.props.circumstances).includes(circumstance.LACTATING_WOMAN)
      ) {
        problems.contradictions.push("Postklimakterium und gleichzeitig stillend");
      }
    }

    if (problems.unloadableDiseases?.length || problems.unloadableDiseasesSolved?.length) {
      problems.level = "error";
    } else if (problems.contradictions?.length) {
      problems.level = "warning";
    }

    return problems;
  });

  const isMergeRecordTransfer = computed(() => {
    return props?.transfer?.scope === "MergeRecord";
  });

  provide("isMergeRecordTransfer", isMergeRecordTransfer);

  const getRowOriginFromMergeLog = (type, id) => {
    // early exit
    if (!isMergeRecordTransfer.value) {
      return null;
    }

    const mergeLogForRecord = props.transfer.meta.find((mergeLog) => mergeLog[type] && mergeLog[type].includes(id));

    return {
      record_id: mergeLogForRecord?.record_id,
      origin: mergeLogForRecord?.origin,
      label: mergeLogForRecord?.label,
      created_at: mergeLogForRecord?.created_at,
    };
  };

  provide("getRowOriginFromMergeLog", getRowOriginFromMergeLog);

  const isUpdating = computed(
    () =>
      isMassUpdateProcessing.value.medication ||
      isMassUpdateProcessing.value.symptom ||
      isMassUpdateProcessing.value.laborValue ||
      isMassUpdateProcessing.value.disease,
  );

  const analysisButtonDisabled = computed(() => {
    return (
      // disable button on level "error" only ("warning" is ok)
      medicationProblems.value.level === "error" ||
      symptomProblems.value.level === "error" ||
      diseaseProblems.value.level === "error" ||
      laborvalueProblems.value.level === "error" ||
      isUpdating.value
    );
  });

  watch(
    () => props.record.info,
    () => {
      recordData.info = props.record.info;
    },
  );

  watch(
    () => props.medications?.data || props.medications?.data.length,
    () => {
      if (props.medications !== null && typeof props.medications === "object" && props.medications.data !== undefined) {
        recordData.medications = props.medications.data;
      }
    },
  );

  watch(
    () => props.symptoms?.data || props.symptoms?.data.length,
    () => {
      if (props.symptoms !== null && typeof props.symptoms === "object" && props.symptoms.data !== undefined) {
        recordData.symptoms = props.symptoms.data;
      }
    },
  );

  watch(
    () => props.diseases?.data || props.diseases?.data.length,
    () => {
      if (props.diseases !== null && typeof props.diseases === "object" && props.diseases.data !== undefined) {
        recordData.diseases = props.diseases.data;

        initAfterRecordHasBeenChanged();
      }
    },
  );

  watch(
    () => props.laborvalues?.data || props.laborvalues?.data.length,
    () => {
      if (props.laborvalues !== null && typeof props.laborvalues === "object" && props.laborvalues.data !== undefined) {
        recordData.laborvalues = props.laborvalues.data;
      }
    },
  );

  watch(
    () => props.isEditable,
    (newValue) => {
      currentIsEditable.value = newValue;
    },
  );

  onBeforeMount(() => {
    privacy.decryptValue(page.props.patient.birthdate).then((birthDate) => {
      patientAge.value = getAge(birthDate);
    });
  });

  onBeforeUnmount(() => {
    broadcast.record.offMessage(props.record.id, handleBroadcastMessage);
  });

  onMounted(() => {
    broadcast.record.onMessage(props.record.id, handleBroadcastMessage);

    setTimeout(() => {
      // in case we are redirected here (after records.store),
      // deferred props are not fetched automatically,
      // but we rely on both of them
      if (!props.laborvalues || !props.transfer) {
        router.reload({
          only: ["laborvalues", "transfer"],
        });
      }
    }, 2000);
  });

  function initAfterRecordHasBeenChanged() {
    recordForm.username = props.record.username ?? privacy.getCurrentUser()?.name;
    recordForm.userstatus = props.record.userstatus ?? "pharmacist";

    const check = props.diseases?.data.some(
      (disease) =>
        disease.abdata_key_miv === circumstance.OLDER_PATIENT ||
        disease.abdata_key_miv === circumstance.GERIATRIC_PATIENT,
    );

    privacy.decryptValue(props.patient.birthdate).then((birthDate) => {
      patientAge.value = getAge(birthDate);

      if (
        props.record.created_at === props.record.updated_at_datetime &&
        !check &&
        recordData.previous_record_id === null && // only newly created records should check for missing age disease
        !isMergeRecordTransfer.value // do not perform these checks when merging records
      ) {
        if (patientAge.value >= 65 && patientAge.value < 75) {
          addAgeDisease(circumstance.OLDER_PATIENT);
        } else if (patientAge.value >= 75) {
          addAgeDisease(circumstance.GERIATRIC_PATIENT);
        }
      }
    });

    currentUser.value = privacy.getCurrentUser();
  }

  const addAgeDisease = (keyMiv) => {
    coreRouter.loadMinCData(
      {filter: "diseases", "q[key_miv]": keyMiv},
      {
        onSuccess: (response) => {
          if (response.data.length === 1) {
            addDisease(props.patient.id, props.record.id, response.data[0], {
              // preserve via and failed props during the
              // diseases.store request...
              via: props.via,
              failed: props.failed,
            });
          }
        },
      },
    );
  };

  const handleBroadcastMessage = (data) => {
    switch (data?.action) {
      case "analyze":
        currentIsEditable.value = false;
        props.record.progress = "resultHasBeenCreated";
        props.record.show_url = globalThis.location.href.replace("/edit", "");
        break;
      case "reset":
        currentIsEditable.value = true;
        break;
      case "reload.record":
        router.reload();
        break;
    }
  };

  const canExportJson = computed(() => page.props.can.json_export);

  const setActive = (stepId) => {
    // show invalid dosage dialog
    if (stepId !== "medicationbar" && invalidDosageFields.value.length > 0) {
      refInvalidDosageConfirmationDialog.value.open();
    } else {
      rememberForm.value.active = stepId;
    }
  };

  const saveRecord = () => {
    recordForm.patch(route("records.update", {patient: props.patient?.id, record: props.record?.id}), {
      only: ["flash", "errors", "record"],
    });
  };

  const changeValue = (newValue, key) => {
    recordForm[key] = newValue;
  };

  const changeIsEditable = () => {
    refEditAgainDialog.value.open();
  };

  const barMessage = (recordKey, valueKey) => {
    let message = [];

    each(props[recordKey]?.data, (value) => {
      message.push(value[valueKey]);
    });

    return message.join(", ");
  };

  const medicationBarMessage = () => {
    let message = [];

    each(props.medications?.data, (value) => {
      message.push(value["fam"]);
    });

    return message.join(", ");
  };

  const laborBarMessage = () => {
    let message = [];

    each(props.laborvalues?.data, (value) => {
      // skip "default" laborvalues, if they do not have a value
      // and if they have no or "normal" deviation
      if (
        (value.mc_laborvalue_key === bmiKey ||
          value.mc_laborvalue_key === pulseKey ||
          value.mc_laborvalue_key === bloodpressureKey ||
          value.mc_laborvalue_key === bodySizeKey ||
          value.mc_laborvalue_key === bodyWeightKey ||
          value.mc_laborvalue_key === creatinineKey ||
          value.mc_laborvalue_key === gfrKey) &&
        value.value === null &&
        (value.deviation === "normal" || value.deviation === null)
      ) {
        return;
      }
      // skip other laborvalues, if they do not have a value or a deviation
      else if (value.value === null && (value.deviation === "normal" || value.deviation === null)) {
        return;
      }

      message.push(buildLaborDetailMessage(value));
    });

    return message.join(", ");
  };

  const buildLaborDetailMessage = (value) => {
    let message = value.type.replace(/\((.*?)\)/, "");

    if (value.deviation && value.deviation.includes(value.deviation)) {
      switch (value.deviation) {
        case "raised": {
          message += " (↑)";
          break;
        }
        case "normal": {
          message += " (norm.)";
          break;
        }
        case "lowered":
          message += " (↓)";
          break;
      }
    }

    if (value.value !== null && value.mc_laborvalue_key) {
      message += " " + value.value.replace(".", ",") + " " + value.unit;
    }

    return message;
  };

  const setDoneAndAnalyze = () => {
    refRecordEditFinishedDialog.value.open();
  };

  const closeAndEmitAnalysisStart = () => {
    Window.open(route("execution.show", {patient: page.props.patient.id, record: page.props.record.id}), "_blank")
      .then(() => {
        broadcast.record.postMessage(props.record.id, {action: "analyze"});

        refRecordEditFinishedDialog.value.close();
      })
      .catch(() => {
        console.error("Execution open failed!");
      });
  };

  const openShareStatusDialog = () => {
    refShareStatusDialog.value.open();
  };

  const openDeleteRowDialog = (data, overrideLength = null) => {
    const payloadValue =
      data.payload?.medication ?? data.payload?.symptom ?? data.payload?.laborvalue ?? data.payload?.disease ?? [];

    const payloadLength = overrideLength ?? (Array.isArray(payloadValue) ? payloadValue.length : 1);

    medicationDeleteConfirmationDialogText.value =
      payloadLength > 1
        ? `Die ausgewählten ${payloadLength} Zeilen werden gelöscht.`
        : "Die ausgewählte Zeile wird gelöscht.";
    refMedicationDeleteConfirmationDialog.value.open(data);
  };

  const loadOnly = (routeName) => {
    const loadOnly = [routeName, "flash"];
    switch (routeName) {
      case "laborvalues":
        loadOnly.push("symptoms", "diseases");
        break;
      case "diseases":
        loadOnly.push("circumstances", "symptoms");
        break;
      case "medications":
        loadOnly.push("symptoms", "diseases");
        break;
    }

    return loadOnly;
  };

  const performSingleDelete = (routeName, payload) => {
    const singularRouteName = routeName.replace(/s$/, "");
    const itemId = payload[singularRouteName];

    router.delete(
      route(`${routeName}.destroy`, {
        patient: page.props.patient.id,
        record: page.props.record.id,
        ...payload,
      }),
      {
        preserveScroll: true,
        only: loadOnly(routeName),
        optimistic: (pageProperties) => {
          const propertyData = pageProperties[routeName];
          if (!propertyData?.data) return {};

          return {
            [routeName]: {
              ...propertyData,
              data: propertyData.data.filter((item) => item.id !== itemId),
            },
          };
        },
        onError: (error) => {
          console.error(error);
        },
      },
    );
  };

  const performMassDelete = (routeName, payload) => {
    const ids = payload ?? [];

    router.post(
      route(`${routeName}.mass-destroy`, {
        patient: page.props.patient.id,
        record: page.props.record.id,
      }),
      {
        ids,
      },
      {
        preserveScroll: true,
        only: loadOnly(routeName),
        optimistic: (pageProperties) => {
          const propertyData = pageProperties[routeName];
          if (!propertyData?.data) return {};

          return {
            [routeName]: {
              ...propertyData,
              data: propertyData.data.filter((item) => !ids.includes(item.id)),
            },
          };
        },
        onError: (error) => {
          console.error(error);
        },
      },
    );
  };

  const deleteRow = ({payload, routeName}) => {
    const singularRouteName = routeName.replace(/s$/, "");

    if (Array.isArray(payload[singularRouteName])) {
      performMassDelete(routeName, payload[singularRouteName]);
    } else {
      performSingleDelete(routeName, payload);
    }
  };

  const handleValidDosage = (dosageId) => {
    invalidDosageFields.value = invalidDosageFields.value.filter((id) => id !== dosageId);
  };

  const handleInvalidDosage = (dosageId, callback) => {
    if (!invalidDosageFields.value.includes(dosageId)) {
      callback();

      invalidDosageFields.value.push(dosageId);
    }
  };

  const setIsMassUpdateProcessing = (variableName, newValue) => {
    isMassUpdateProcessing.value[variableName] = newValue;
  };

  const getIsMassUpdateProcessing = (variableName) => {
    return isMassUpdateProcessing.value[variableName];
  };

  const confirmMergeRecord = () => {
    refConfirmMergeRecord.value.open();
  };

  const completeMergeRecord = () => {
    router.post(
      route("complete-merge-record", {patient: props.patient.id, record: props.record.id}),
      {},
      {
        preserveState: false, // important to force reload
        preserveScroll: false,
      },
    );
  };

  provide("recordEditDeleteRowDialog", openDeleteRowDialog);
  provide("recordEditHandleValidDosage", handleValidDosage);
  provide("recordEditHandleInvalidDosage", handleInvalidDosage);

  provide("setIsMassUpdateProcessing", setIsMassUpdateProcessing);
  provide("getIsMassUpdateProcessing", getIsMassUpdateProcessing);
</script>

<template>
  <ComponentBreadcrumb
    :links="[
      {
        label: 'Patienten',
        url: patient.is_fake ? $route('patients.index', {filter: 'shared-with-me'}) : $route('patients.index'),
      },
      {encryptedLabel: [patient.firstname, patient.lastname], url: $route('patients.show', {patient: patient.id})},
      {
        label: 'Eingabe',
        subLabel: {
          component: 'component-record-label',
          props: {
            record: record,
          },
        },
      },
    ]"
  />

  <section class="relative mt-6 flex items-start space-x-6">
    <div class="sticky top-20 z-10">
      <InertiaDeferred :data="['transfer']">
        <template #fallback />
        <ComponentPatientSidebar :patient="patient" :record="record" :transfer="transfer" />
      </InertiaDeferred>
    </div>

    <article class="flex max-w-(--breakpoint-2xl) flex-1 flex-col">
      <div class="flex-1">
        <div class="flex items-center justify-between p-4">
          <h2 class="text-2xl font-semibold">Medikationsanalyse</h2>

          <div class="flex space-x-1">
            <span class="m-auto mr-2 text-xs">
              Gespeichert:
              <span class="text-xs" v-text="record.updated_at_datetime" />
            </span>

            <InertiaDeferred data="transfer">
              <template #fallback>
                <SkeletonLoader class="size-8" />
                <SkeletonLoader class="size-8" />
                <SkeletonLoader class="size-8" />
              </template>

              <ComponentRecordProgress :record="record" :transfer="transfer" />
            </InertiaDeferred>
          </div>
        </div>

        <div class="mt-4">
          <RecordComponentSectionUserBar
            :record="record"
            :is-editable="currentIsEditable"
            :change-value="changeValue"
            :save-record="saveRecord"
            :test-id="getTestId()"
          />
        </div>

        <ComponentAlert
          v-if="isMergeRecordTransfer"
          type="info"
          class="mt-8"
          :test-id="getTestId('merge-record-in-progress')"
        >
          <p>
            <strong class="font-semibold">Sie befinden sich im Ansichtsmodus "Zusammenführen von Varianten"</strong>
          </p>
          <p class="mt-2">
            Da alle Inhalte in diese zusammengeführte Variante kopiert wurden, können widersprüchliche Angaben vorhanden
            sein.
          </p>
          <ul class="mt-2 ml-8 list-disc space-y-1">
            <li>
              Bitte überprüfen Sie alle Bereiche auf Dopplungen und Abweichungen, bspw. gleiche Arzneimittel mit
              unterschiedlicher Dosierung.
            </li>
            <li>Korrigieren bzw. löschen Sie widersprüchliche Angaben.</li>
            <li>
              Zur Orientierung ist jede Zeile mit

              <div
                class="inline-flex items-center divide-x-2 divide-mcblue-400 rounded-md bg-mcblue-100 p-1 text-xs font-medium text-mcblue [&>span]:px-1"
              >
                <span class="inline-flex items-center gap-x-1.5">
                  <ComponentIcon>edit</ComponentIcon>
                  Herkunft
                </span>
                <span>ggf. Bezeichnung</span>
                <span>Datum</span>
              </div>
              der ursprünglichen Analyse gekennzeichnet.
            </li>
          </ul>
        </ComponentAlert>

        <ComponentAlert
          v-if="hasMissingConfirmation && !isMergeRecordTransfer"
          type="info"
          class="mt-8"
          :test-id="getTestId('sign-patient-documents')"
        >
          <p>
            Es liegen noch nicht alle

            <MedLink
              variant="primary"
              icon="launch"
              icon-align="end"
              :href="$route('patients.edit', {patient: patient.id}) + '#patient-documents'"
              target="_blank"
              :data-test="getTestId('patient-documents-link')"
            >
              Einwilligungen
            </MedLink>
            des Patienten vor.
          </p>
        </ComponentAlert>

        <ComponentAlert
          v-if="typeof patientAge === 'number' && patientAge < 18 && !isMergeRecordTransfer"
          :allow-closing="false"
          type="warning"
          class="mt-8"
          :test-id="getTestId('age')"
        >
          <h4 class="font-bold">Achtung</h4>
          <p class="mt-2">
            Kinder und Jugendliche bis 18 Jahre werden in den Prüfungen des MediCheck nicht ausreichend berücksichtigt.
            Unter anderem werden den Altersbereich betreffende Dosierungen nicht automatisiert überprüft. Genauere
            Informationen entnehmen Sie der Gebrauchsanweisung.
          </p>
        </ComponentAlert>

        <ComponentAlert
          v-if="via && via.startsWith('import') && !isMergeRecordTransfer"
          :allow-closing="false"
          type="warning"
          class="mt-8"
          :test-id="getTestId('import')"
        >
          <h4 class="font-bold">Importierte Medikationsanalyse</h4>
          <p class="mt-2">
            Bitte überprüfen Sie bei einer importierten Medikationsanalyse
            <span v-if="via === 'import-bmp'">(BMP),</span>

            <span v-else-if="via === 'import-json'">(JSON-Datei),</span>

            <span v-else-if="via === 'import-wawi'">(WaWi),</span>
            ob alle Informationen vollständig übernommen wurden.
          </p>

          <template v-if="failed?.length > 0">
            <p class="mt-3">
              Bitte beachten Sie, dass die Medikation unvollständig importiert wird, da folgende PZNs/FAMs keinem
              Arzneimittel zugeordnet werden können. Bitte ergänzen Sie diese Arzneimittel manuell.
            </p>

            <ul class="list-dash">
              <li v-for="(pzn, index) in failed" :key="index" v-text="pzn" />
            </ul>
          </template>
        </ComponentAlert>

        <ComponentAlert
          v-else-if="via && via === 'patientinput' && !isMergeRecordTransfer"
          allow-closing
          type="warning"
          class="mt-8"
          :test-id="getTestId('patient-input')"
        >
          <h4 class="font-bold">
            Daten aus
            <template v-if="record.origin === recordOriginKeys.carerinput">{{ recordOriginTexts.carerinput }}</template>

            <template v-else>{{ recordOriginTexts.patientinput }}</template>
          </h4>

          <p>
            Diese Medikationsanalyse wurde mit Daten aus einem Fragebogen vorbefüllt.

            <template v-if="record.origin === recordOriginKeys.carerinput">
              Daten zum Heim, zu individuellen Besonderheiten und Kontaktperson finden Sie ggf. in der linken
              Seitenleiste.
            </template>

            <template v-else>
              Die Adhärenz-Antworten, ggf. individuelle Besonderheiten und Kontaktperson finden Sie in der linken
              Seitenleiste.
            </template>
          </p>

          <template v-if="record.metadata.patientinput.failed?.length > 0">
            <p class="mt-3">
              Bitte beachten Sie, dass die Medikation unvollständig importiert wird, da folgende PZNs/FAMs keinem
              Arzneimittel zugeordnet werden können. Bitte ergänzen Sie diese Arzneimittel manuell.
            </p>

            <ul class="list-dash">
              <li v-for="(pzn, index) in record.metadata.patientinput.failed" :key="index" v-text="pzn" />
            </ul>
          </template>
        </ComponentAlert>

        <InertiaDeferred data="sharedata">
          <template #fallback />

          <PagesRecordGeneralShareStatus
            v-if="$page.props.client.license.is_pro"
            :patient="patient"
            :record="record"
            :sharedata="sharedata"
            @open:share-status-dialog="openShareStatusDialog()"
          />
        </InertiaDeferred>

        <div id="form-components" class="mt-8 bg-white pt-8 shadow-md">
          <ComponentStepper
            is-defer
            defer-prop-name="medications"
            step-id="medicationbar"
            step-label="Medikation"
            :step-description="medicationBarMessage()"
            step-number="1"
            :active="rememberForm.active"
            :warning="medicationProblems.level !== null"
            :test-id="getTestId('medication')"
            @click="setActive('medicationbar')"
          >
            <template #content>
              <RecordComponentSectionMedicationBar
                :is-editable="currentIsEditable"
                :change-is-editable="changeIsEditable"
                :record-data="recordData"
                :problems="medicationProblems"
                :test-id="getTestId('medication')"
              />
            </template>
          </ComponentStepper>

          <ComponentStepper
            is-defer
            defer-prop-name="symptoms"
            step-id="symptombar"
            step-label="Probleme / Symptome"
            :step-description="barMessage('symptoms', 'symptom')"
            step-number="2"
            :active="rememberForm.active"
            :warning="symptomProblems.level !== null"
            :test-id="getTestId('symptom')"
            @click="setActive('symptombar')"
          >
            <template #content>
              <RecordComponentSectionSymptomBar
                :is-editable="currentIsEditable"
                :change-is-editable="changeIsEditable"
                :record-data="recordData"
                :problems="symptomProblems"
                :test-id="getTestId('symptom')"
              />
            </template>
          </ComponentStepper>

          <ComponentStepper
            is-defer
            defer-prop-name="laborvalues"
            step-id="laborbar"
            step-label="Labor- / Vitalwerte"
            :step-description="laborBarMessage()"
            :optional-stepper="true"
            step-number="3"
            :active="rememberForm.active"
            :warning="laborvalueProblems.level !== null"
            :test-id="getTestId('labor')"
            @click="setActive('laborbar')"
          >
            <template #content>
              <RecordComponentSectionLaborBar
                :is-editable="currentIsEditable"
                :change-is-editable="changeIsEditable"
                :record-data="recordData"
                :problems="laborvalueProblems"
                :test-id="getTestId('labor')"
              />
            </template>
          </ComponentStepper>

          <ComponentStepper
            is-defer
            defer-prop-name="diseases"
            step-id="diseasebar"
            step-label="Erkrankungen / Allergien / Lebensumstände"
            :step-description="barMessage('diseases', 'disease')"
            :optional-stepper="true"
            step-number="4"
            :active="rememberForm.active"
            :warning="diseaseProblems.level !== null"
            :last-step="true"
            :test-id="getTestId('disease')"
            @click="setActive('diseasebar')"
          >
            <template #content>
              <RecordComponentSectionDiseaseBar
                :is-editable="currentIsEditable"
                :change-is-editable="changeIsEditable"
                :record-data="recordData"
                :problems="diseaseProblems"
                :test-id="getTestId('disease')"
              />
            </template>
          </ComponentStepper>
        </div>

        <ComponentCard v-if="currentIsEditable || canExportJson" class="mt-6" :test-id="getTestId('')">
          <div v-if="currentIsEditable" class="grid">
            <p v-if="analysisButtonDisabled && !isUpdating" class="justify-self-center text-red-500">
              Bitte überprüfen Sie ungültige oder veraltete Datensätze in den oben markierten Bereichen.
            </p>

            <template v-if="isMergeRecordTransfer">
              <div v-if="!analysisButtonDisabled" class="justify-self-center">
                <p class="max-w-md">
                  Wenn Sie das
                  <strong class="font-semibold">Zusammenführen von Varianten abschließen,</strong>
                  öffnet sich die Eingabemaske im normalen Modus.
                </p>
                <p class="mt-2">Dort können Sie dann wie gewohnt die Analyse starten.</p>
              </div>

              <MedButton
                variant="secondary"
                class="mt-2 w-fit justify-self-center"
                :data-test="getTestId('confirm-merge-record-button')"
                :disabled="analysisButtonDisabled"
                @click="confirmMergeRecord"
              >
                Zusammenführen abschließen
              </MedButton>
            </template>

            <template v-else>
              <MedButton
                variant="primary"
                class="w-fit justify-self-center"
                :disabled="analysisButtonDisabled"
                :data-test="getTestId('save-button')"
                @click="setDoneAndAnalyze"
              >
                Speichern und analysieren
              </MedButton>
            </template>
          </div>

          <div v-if="canExportJson" class="grid justify-center py-3">
            <RecordComponentJsonExporter :test-id="getTestId()" />
          </div>
        </ComponentCard>
      </div>

      <!-- Dialog for before analyse -->
      <RecordComponentBeforeAnalyseDialog
        ref="refRecordEditFinishedDialog"
        :analysis-button-disabled="analysisButtonDisabled"
        :test-id="getTestId()"
        @close-and-emit-analysis-start="closeAndEmitAnalysisStart"
      />
    </article>
  </section>

  <!-- Dialogs -->
  <InertiaDeferred data="sharedata">
    <template #fallback />
    <RecordComponentEditAgainDialog
      ref="refEditAgainDialog"
      :patient="patient"
      :record="record"
      :sharedata="sharedata"
      :test-id="getTestId()"
    />
  </InertiaDeferred>

  <ComponentRecordInvalidatedDialog :record="record" :ignore-reset="true" />

  <ComponentConfirmationDialog
    ref="refMedicationDeleteConfirmationDialog"
    title="Sind Sie sicher?"
    :content="medicationDeleteConfirmationDialogText"
    @confirmed="deleteRow"
  />

  <ComponentDialog
    ref="refInvalidDosageConfirmationDialog"
    dialog-title="Ungültige Medikation"
    dialog-content="Bitte korrigieren Sie die markierten Dosierungsangaben."
    dialog-type="information"
    primary-label="OK"
    :test-id="getTestId()"
  />

  <ComponentConfirmationDialog
    ref="refConfirmMergeRecord"
    title="Zusammenführen abschließen"
    content="Sie beenden den aktuellen Ansichtsmodus und können anschließend die Analyse starten."
    @confirmed="() => completeMergeRecord()"
  />

  <InertiaDeferred :data="['sharedata', 'notifications']">
    <template #fallback />
    <ComponentRecordShareStatusDialog
      ref="refShareStatusDialog"
      :record="record"
      :patient="patient"
      :sharedata="sharedata"
      :notifications="notifications"
    />
  </InertiaDeferred>
</template>
